import { ClientLoggingService } from '../shared/client-logging.service';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { Tab } from './models/tab.model';
import { OverviewComponent } from '../components/project/pages/overview/overview.component';
import { PreviewComponent } from '../components/project/pages/preview/preview.component';
import { ComplianceComponent } from '../components/project/pages/compliance/compliance.component';
import { ComplianceReportComponent } from '../components/project/pages/compliance/compliance-report-tab/compliance-report-tab.component';
import { ReferencesComponent } from '../components/project/pages/references/references.component';
import { LoadingComponent } from '../components/shared/ui/loading/loading.component';
import { ArchitecturalPlanPage } from './models/architectural-plan-page.model';
import { RegionHierarchyTreeNode } from '../../generated.commonjs/api_pb';
import { HighlightRegion, HighlightUnits, NO_HIGHLIGHT_REGIONS, unionBox } from './highlight-regions';
import {
  clampFeatherPercent,
  DEFAULT_FEATHER_PERCENT,
} from '../components/project/pages/preview/spotlight-overlay/spotlight-feather';

/**
 * Bounding box for spotlight highlight (Issue #292).
 */
export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Tracks the active plan page and selected tab across project views, emitting changes via observables. */
@Injectable({
  providedIn: 'root',
})
export class TabService {
  private static readonly BASE_TABS: Readonly<Tab[]> = [
    { label: 'Overview', component: OverviewComponent, tabId: 'overview' },
    { label: 'Preview', component: PreviewComponent, tabId: 'preview' },
    { label: 'Compliance', component: ComplianceComponent, tabId: 'compliance' },
  ];

  private static readonly POWER_USER_TABS: Readonly<Tab[]> = [
    { label: 'References', component: ReferencesComponent, tabId: 'references' },
  ];

  private static readonly STATIC_TABS: Readonly<Tab[]> = [...TabService.BASE_TABS, ...TabService.POWER_USER_TABS];

  private staticTabCount = TabService.BASE_TABS.length;
  static readonly STATIC_TAB_IDS = TabService.STATIC_TABS.map(t => t.tabId);

  private tabs: Tab[] = [...TabService.BASE_TABS];

  private tabsSubject = new BehaviorSubject<Tab[]>(this.tabs);
  tabs$ = this.tabsSubject.asObservable();

  private activeTabIndexSubject = new BehaviorSubject<number>(0);
  activeTabIndex$ = this.activeTabIndexSubject.asObservable();

  private newTabAddedSubject = new Subject<{ tabIndex: number; tabId: string }>();
  newTabAdded$ = this.newTabAddedSubject.asObservable();

  private activePlanPageSubject = new BehaviorSubject<ArchitecturalPlanPage | null>(null);
  activePlanPage$ = this.activePlanPageSubject.asObservable();

  private activeTabDataSubject = new BehaviorSubject<any>(null);
  activeTabData$ = this.activeTabDataSubject.asObservable();

  /**
   * Code-section titles from the table of contents the compliance page has already loaded, keyed by
   * section number (Issue #1773).
   *
   * Published here rather than fetched where it is used, because the contents arrive once for the
   * whole page and a report can name a dozen subsections; a lookup per link would turn hovering into
   * a series of requests. Starts empty, which renders number-only tooltips until the page has loaded
   * its tree — a correct state rather than a placeholder.
   */
  private codeSectionTitlesSubject = new BehaviorSubject<ReadonlyMap<string, string>>(new Map());
  codeSectionTitles$ = this.codeSectionTitlesSubject.asObservable();

  // Highlight box for spotlight overlay (Issue #292)
  private highlightBoxSubject = new BehaviorSubject<BoundingBox | null>(null);
  highlightBox$ = this.highlightBoxSubject.asObservable();

  // The individual regions of the highlight, each with an optional label (Issue #2254).
  // `highlightBox$` carries the box that contains them all, so code that knows about one box
  // only (camera framing, dismissal) keeps working unchanged.
  private highlightRegionsSubject = new BehaviorSubject<readonly HighlightRegion[]>(NO_HIGHLIGHT_REGIONS);
  highlightRegions$ = this.highlightRegionsSubject.asObservable();

  /**
   * The page rotation the current highlight's boxes were measured at, when the link said so (Issue
   * #2445). `null` means the link did not say; the Preview then takes the boxes to match the page as
   * it is displayed when the link is first shown.
   */
  private highlightRotation: number | null = null;
  /** Whether the current link's boxes are PDF points or normalized 0–1000 coordinates. */
  private highlightUnits: HighlightUnits = 'points';

  // Active region selection (Issue #1228)
  private activeRegionSubject = new BehaviorSubject<RegionHierarchyTreeNode.AsObject | null>(null);
  activeRegion$ = this.activeRegionSubject.asObservable();
  private powerUserModeEnabled = false;

  // Hovered region for preview highlight on TOC hover (Issue #1228)
  private hoveredRegionSubject = new BehaviorSubject<RegionHierarchyTreeNode.AsObject | null>(null);
  hoveredRegion$ = this.hoveredRegionSubject.asObservable();

  // Configurable spotlight feather, as a percentage of the highlighted box's
  // on-screen (width + height). Default 5%, stored per-project in localStorage
  // under `highlightFeatherPct_<projectId>` (Issue #1496).
  private highlightFeatherPctSubject = new BehaviorSubject<number>(DEFAULT_FEATHER_PERCENT);
  highlightFeatherPct$ = this.highlightFeatherPctSubject.asObservable();

  // Per-project browser preference for the compact High/Medium warning counts in the table of
  // contents. Operational states such as Pending or Failed are not controlled by this preference.
  private tableOfContentsWarningsSubject = new BehaviorSubject<boolean>(true);
  tableOfContentsWarnings$ = this.tableOfContentsWarningsSubject.asObservable();

  /** localStorage key prefix for the per-project spotlight feather percentage. */
  private static readonly HIGHLIGHT_FEATHER_PCT_KEY_PREFIX = 'highlightFeatherPct_';
  private static readonly TABLE_OF_CONTENTS_WARNINGS_KEY_PREFIX = 'tocComplianceWarnings_';

  /**
   * Enables or disables Power User Mode tabs (Issue #1228).
   * When enabled, the References tab is included in the static tab set.
   */
  setPowerUserMode(enabled: boolean): void {
    this.powerUserModeEnabled = enabled;
    const activeTab = this.tabs[this.getActiveTabIndex()];
    const dynamicTabs = this.tabs.slice(this.staticTabCount);
    const baseTabs = [...TabService.BASE_TABS];

    if (enabled) {
      baseTabs.push(...TabService.POWER_USER_TABS);
    }

    this.staticTabCount = baseTabs.length;
    this.tabs = [...baseTabs, ...dynamicTabs];
    this.tabsSubject.next(this.tabs);

    // Issue #1878: adding or removing References shifts every report tab by one position, so keep
    // the same tab selected rather than the same index. If References itself was selected and has
    // just been removed, fall back to the Compliance tab.
    const activeIndex = activeTab ? this.tabs.indexOf(activeTab) : -1;
    if (activeIndex === -1) {
      this.setActiveTab(TabService.BASE_TABS.length - 1);
    } else if (activeIndex !== this.getActiveTabIndex()) {
      this.activeTabIndexSubject.next(activeIndex);
    }

    ClientLoggingService.log(
      'INFO',
      `[TabService] Power user mode ${enabled ? 'enabled' : 'disabled'}, static tabs: ${this.staticTabCount}`
    );
  }

  setActivePlanPage(page: ArchitecturalPlanPage | null) {
    this.activeRegionSubject.next(null);
    this.activePlanPageSubject.next(page);
  }

  /** Sets the active page without clearing the active region. */
  setActivePlanPageKeepRegion(page: ArchitecturalPlanPage | null) {
    this.activePlanPageSubject.next(page);
  }

  setActiveRegion(region: RegionHierarchyTreeNode.AsObject | null) {
    this.activeRegionSubject.next(region);
    this.updateTabsForRegionMode(region != null);
  }

  /**
   * When a region is active, hide Compliance tab (regions don't have compliance data).
   * When deactivated, restore full tab set.
   */
  private updateTabsForRegionMode(_regionActive: boolean): void {
    // Region selection no longer changes the top-level tab set.
    // The Compliance tab stays visible (page-level data) regardless of
    // whether a sub-region is selected. The Overview component hides
    // page-level content and shows region details when a region is active.
  }

  isPowerUserModeEnabled(): boolean {
    return this.powerUserModeEnabled;
  }

  getActiveRegion(): RegionHierarchyTreeNode.AsObject | null {
    return this.activeRegionSubject.getValue();
  }

  setHoveredRegion(region: RegionHierarchyTreeNode.AsObject | null): void {
    this.hoveredRegionSubject.next(region);
  }

  /** Sets the spotlight feather percentage (not persisted). */
  setHighlightFeatherPct(pct: number): void {
    this.highlightFeatherPctSubject.next(clampFeatherPercent(pct));
  }

  /** Current spotlight feather percentage. */
  getHighlightFeatherPct(): number {
    return this.highlightFeatherPctSubject.getValue();
  }

  /**
   * Loads the spotlight feather percentage from localStorage for a given project.
   * Falls back to the default when absent or unparseable. The legacy pixel key
   * (`highlightBuffer_<projectId>`) is intentionally ignored — the unit changed and
   * this is a per-project cosmetic preference, so no migration is needed (Issue #1496).
   */
  loadHighlightFeatherPct(projectId: string): void {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(`${TabService.HIGHLIGHT_FEATHER_PCT_KEY_PREFIX}${projectId}`);
    } catch (err) {
      // localStorage can throw SecurityError where storage is blocked (cookies
      // disabled, some iframe/webview contexts). Fall through to the default so
      // startup is never blocked by an unreadable cosmetic preference.
      ClientLoggingService.log('WARN', '[TabService] Failed to read highlight feather from localStorage:', err);
    }

    const val = stored === null ? NaN : Number.parseFloat(stored);
    this.highlightFeatherPctSubject.next(Number.isFinite(val) ? clampFeatherPercent(val) : DEFAULT_FEATHER_PERCENT);
  }

  /** Saves the spotlight feather percentage to localStorage for a given project. */
  saveHighlightFeatherPct(projectId: string, pct: number): void {
    const clamped = clampFeatherPercent(pct);

    try {
      localStorage.setItem(`${TabService.HIGHLIGHT_FEATHER_PCT_KEY_PREFIX}${projectId}`, String(clamped));
    } catch (err) {
      // QuotaExceededError / SecurityError must not break the slider interaction.
      ClientLoggingService.log('WARN', '[TabService] Failed to save highlight feather to localStorage:', err);
    }

    // Emitted outside the try so the in-memory value and the live preview update
    // even when the preference could not be persisted.
    this.highlightFeatherPctSubject.next(clamped);
  }

  /** Current table-of-contents warning visibility. */
  getTableOfContentsWarningsVisible(): boolean {
    return this.tableOfContentsWarningsSubject.getValue();
  }

  /**
   * Loads the table-of-contents warning preference for one project.
   *
   * This is a cosmetic, per-browser choice. Missing or invalid data defaults to visible so an
   * existing reviewer does not silently lose compliance warnings after upgrading.
   */
  loadTableOfContentsWarnings(projectId: string): void {
    let visible = true;
    try {
      const stored = localStorage.getItem(`${TabService.TABLE_OF_CONTENTS_WARNINGS_KEY_PREFIX}${projectId}`);
      if (stored === 'false') {
        visible = false;
      }
    } catch (err) {
      ClientLoggingService.log(
        'WARN',
        '[TabService] Failed to read table-of-contents warning preference from localStorage:',
        err
      );
    }
    this.tableOfContentsWarningsSubject.next(visible);
  }

  /** Saves and immediately applies the table-of-contents warning preference for one project. */
  saveTableOfContentsWarnings(projectId: string, visible: boolean): void {
    try {
      localStorage.setItem(`${TabService.TABLE_OF_CONTENTS_WARNINGS_KEY_PREFIX}${projectId}`, String(visible));
    } catch (err) {
      ClientLoggingService.log(
        'WARN',
        '[TabService] Failed to save table-of-contents warning preference to localStorage:',
        err
      );
    }
    this.tableOfContentsWarningsSubject.next(visible);
  }

  /** Publishes the section titles the compliance tree was built from. */
  setCodeSectionTitles(titles: ReadonlyMap<string, string>) {
    this.codeSectionTitlesSubject.next(titles);
  }

  setActiveTabData(data: any) {
    ClientLoggingService.log('INFO', '[TabService] Setting active tab data:', data);
    this.activeTabDataSubject.next(data);
  }

  setHighlightBox(box: BoundingBox | null) {
    ClientLoggingService.log('INFO', '[TabService] Setting highlight box:', box);
    // The regions are published first: a subscriber to `highlightBox$` reads them with
    // `getHighlightRegions()` and must find the ones that belong to the box it was just given.
    this.highlightRotation = null;
    this.highlightUnits = 'points';
    this.highlightRegionsSubject.next(box ? [{ box }] : NO_HIGHLIGHT_REGIONS);
    this.highlightBoxSubject.next(box);
  }

  /**
   * Highlight several regions at once, each with an optional label (Issue #2254).
   * An empty list clears the highlight.
   *
   * @param rotation the page rotation the boxes were measured at, when the link states it
   */
  setHighlightRegions(
    regions: readonly HighlightRegion[],
    rotation: number | null = null,
    units: HighlightUnits = 'points'
  ) {
    ClientLoggingService.log('INFO', '[TabService] Setting highlight regions:', regions);
    // Published before the regions, for the same reason the regions are published before the box.
    this.highlightRotation = regions.length > 0 ? rotation : null;
    this.highlightUnits = regions.length > 0 ? units : 'points';
    this.highlightRegionsSubject.next(regions.length > 0 ? regions : NO_HIGHLIGHT_REGIONS);
    this.highlightBoxSubject.next(unionBox(regions.map(region => region.box)));
  }

  /** The regions of the current highlight; empty when nothing is highlighted. */
  getHighlightRegions(): readonly HighlightRegion[] {
    return this.highlightRegionsSubject.getValue();
  }

  /** The page rotation the current highlight was measured at, or `null` when the link did not say. */
  getHighlightRotation(): number | null {
    return this.highlightRotation;
  }

  /** Units carried by the current highlight link; historic links are points. */
  getHighlightUnits(): HighlightUnits {
    return this.highlightUnits;
  }

  getTabLabel(index: number): string {
    return this.tabs[index]?.tabId || 'overview';
  }

  getTabIndexByTabParam(tabParam: string): number {
    return this.tabs.findIndex(t => t.tabId === tabParam);
  }

  getActiveTabIndex(): number {
    return this.activeTabIndexSubject.getValue();
  }

  setActiveTab(index: number): void {
    if (index >= 0 && index < this.tabs.length) {
      this.activeTabIndexSubject.next(index);

      // Issue #1878: always publish the selected tab's own data, including "none". Keeping the
      // previous value when the new tab has no data left a closed report's data published after the
      // selection had moved to a fixed tab such as Compliance.
      this.setActiveTabData(this.tabs[index].data ?? null);
    }
  }

  addTab(tab: Tab): number {
    ClientLoggingService.log('INFO', '[TabService] Adding tab:', tab);
    this.tabs.push(tab);
    this.tabsSubject.next(this.tabs);
    const newIndex = this.tabs.length - 1;

    // Set the active tab data if the tab has data
    if (tab.data) {
      ClientLoggingService.log('INFO', '[TabService] Tab has data, setting active tab data');
      this.setActiveTabData(tab.data);
    }

    // Defer the tab switch to allow Angular to render the new tab DOM first.
    // This ensures the mat-tab-group animation can properly calculate the
    // slide-in position for the new tab content.
    setTimeout(() => {
      this.activeTabIndexSubject.next(newIndex);
      this.newTabAddedSubject.next({ tabIndex: newIndex, tabId: tab.tabId });
    }, 0);

    return newIndex;
  }

  addLoadingTab(sectionId: string): number {
    const tab: Tab = {
      label: `Loading ${sectionId}...`,
      component: LoadingComponent,
      tabId: sectionId,
    };
    return this.addTab(tab);
  }

  updateTabWithContent(index: number, sectionData: any): void {
    if (index < 0 || index >= this.tabs.length) {
      return;
    }

    const tab = this.tabs[index];
    tab.label = sectionData.title;
    tab.component = ComplianceReportComponent;
    tab.data = sectionData;

    this.tabsSubject.next([...this.tabs]);

    // Set the active tab data if this is the currently active tab
    if (index === this.getActiveTabIndex()) {
      this.setActiveTabData(sectionData);
    }
  }

  /**
   * Removes one dynamic (compliance-report) tab. The fixed application tabs cannot be removed.
   *
   * Issue #1878: the selection follows the tab the user was looking at. Closing a tab to the right
   * of the active one leaves the selection alone, closing one to its left shifts the index down by
   * one so the same report stays open, and closing the active tab selects its left-hand neighbour
   * and publishes that neighbour's data (or none, for a fixed tab).
   */
  removeTab(index: number): void {
    const tab = this.tabs[index];
    if (!tab || !TabService.isDynamicTab(tab)) {
      return;
    }

    const activeIndex = this.getActiveTabIndex();
    this.tabs.splice(index, 1);
    this.tabsSubject.next(this.tabs);

    if (index < activeIndex) {
      this.activeTabIndexSubject.next(activeIndex - 1);
    } else if (index === activeIndex) {
      this.setActiveTab(Math.max(0, index - 1));
    }
  }

  /**
   * Issue #660: Check whether the currently active tab is a dynamic
   * (compliance-report) tab rather than one of the three static tabs.
   */
  isActiveDynamicTab(): boolean {
    return this.getActiveTabIndex() >= this.staticTabCount;
  }

  /**
   * Issue #660: Remove all dynamic compliance-report tabs that were added
   * for a specific page. If the active index pointed to a dynamic tab,
   * reset it to the last static tab (Compliance).
   */
  removeDynamicTabs(): void {
    if (this.tabs.length <= this.staticTabCount) {
      return;
    }
    this.tabs.splice(this.staticTabCount);
    this.tabsSubject.next(this.tabs);

    if (this.getActiveTabIndex() >= this.staticTabCount) {
      this.setActiveTab(this.staticTabCount - 1);
    }
  }

  /**
   * Issue #1878: Returns true for a tab the user opened (a compliance report or its loading
   * placeholder), and false for the fixed Overview, Preview, Compliance and References tabs.
   *
   * Decided by the tab's identity rather than its position, because the number of fixed tabs
   * depends on Power User Mode and a report's position changes as other tabs are closed.
   */
  static isDynamicTab(tab: Tab): boolean {
    return TabService.isDynamicTabParam(tab.tabId);
  }

  /**
   * Issue #660: Returns true when the given tab param is a section-level
   * deep link (i.e. not one of the static tab IDs).
   */
  static isDynamicTabParam(tabParam: string | undefined): boolean {
    return !!tabParam && !TabService.STATIC_TAB_IDS.includes(tabParam);
  }
}
